/**
 * Servicio de evaluación de objetivos académicos usando GPT
 * Evalúa si el usuario cumple objetivos específicos basándose únicamente en su frase actual
 */

import { LessonObjective } from './academicService';
import llmCredentialsService from './llmCredentialsService';
// import configService from './configService';

export interface ObjectiveEvaluationResult {
  description: string;  // Descripción exacta del objetivo
  is_fulfilled: boolean;  // true si se cumplió, false si no
  confidence: number;  // 0.0 a 1.0
}

interface GPTEvaluationResponse {
  objectives: ObjectiveEvaluationResult[];
}

class ObjectiveEvaluationService {
  /**
   * Evalúa objetivos usando GPT basándose únicamente en la frase actual del usuario
   * @param userMessage Frase actual del usuario (no toda la conversación)
   * @param objectives Lista de objetivos a evaluar
   * @returns Array con resultados de evaluación para cada objetivo
   */
  async evaluateObjectivesWithGPT(
    userMessage: string,
    objectives: LessonObjective[]
  ): Promise<ObjectiveEvaluationResult[]> {
    try {
      // Verificar modo de desarrollo
      // const config = configService.getConfig();
      // const isDevelopment = config.testingPhase ?? true; // Por defecto desarrollo

      // Obtener credenciales de OpenAI (llmCredentialsService maneja automáticamente dev vs prod)
      let credentials = llmCredentialsService.getCredentials();
      if (!credentials || !credentials.openaiKey) {
        credentials = await llmCredentialsService.loginAndGetCredentials();
      }

      if (!credentials.openaiKey) {
        throw new Error('No se pudo obtener la API key de OpenAI');
      }

      // Construir prompt con los objetivos (solo usando description)
      const objectivesList = objectives.map(obj => {
        return `- "${obj.description}"`;
      }).join('\n');

      const systemPrompt = `Eres un evaluador de objetivos de aprendizaje de francés. Tu tarea es evaluar si una frase del estudiante cumple con los objetivos específicos.

OBJETIVOS A EVALUAR:
${objectivesList}

INSTRUCCIONES IMPORTANTES:
1. Analiza ÚNICAMENTE la frase del estudiante que recibirás
2. Sé FLEXIBLE con errores de ortografía menores (por ejemplo: "m'apelle" vs "m'appelle", "apelle" vs "appelle")
3. Enfócate en la INTENCIÓN COMUNICATIVA del estudiante, no solo en la ortografía perfecta
4. Si la frase claramente intenta cumplir un objetivo aunque tenga pequeños errores, marca is_fulfilled como true
5. Para cada objetivo, determina si la frase del estudiante lo cumple considerando la intención
6. Responde ÚNICAMENTE con un JSON válido en el siguiente formato:
{
  "objectives": [
    {
      "description": "descripción exacta del objetivo",
      "is_fulfilled": true/false,
      "confidence": 0.0-1.0
    }
  ]
}

7. "is_fulfilled": true si la frase claramente cumple el objetivo (aunque tenga errores menores, fuera del objetivo), false si no hay relación con el objetivo o tiene errores relacionados con él.
8. "confidence": nivel de confianza de tu evaluación (0.0 = muy incierto, 1.0 = muy seguro)
9. Responde SOLO con el JSON, sin texto adicional antes o después
10. Incluye TODOS los objetivos en la respuesta, incluso si is_fulfilled es false

EJEMPLOS:
- "je m'apelle laura" → Presentarse usando s'appeler: is_fulfilled = false (porque tiene un error de ortografía directamente relacionado con el obejtivo)
- "je m'appelle laura e je sui hereux" → Presentarse usando s'appeler: is_fulfilled = true (cumple con el objetivo pese a otros errores de ortografía)
- "bonjour Rosalie" → Usar vocabulario de saludos: is_fulfilled = true`;

      const userPrompt = `Frase del estudiante a evaluar: "${userMessage}"`;

      // Llamar a OpenAI API
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.openaiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',  // Modelo económico para reducir costos
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: userPrompt
            }
          ],
          temperature: 0.3,  // Baja temperatura para respuestas más consistentes
          response_format: { type: 'json_object' }  // Forzar respuesta JSON
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No se recibió contenido de OpenAI');
      }

      // Parsear JSON de respuesta
      let evaluationResponse: GPTEvaluationResponse;
      try {
        evaluationResponse = JSON.parse(content);
      } catch (parseError) {
        // Intentar extraer JSON si hay texto adicional
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          evaluationResponse = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error(`No se pudo parsear JSON de respuesta: ${content}`);
        }
      }

      // Validar que tenga la estructura esperada
      if (!evaluationResponse.objectives || !Array.isArray(evaluationResponse.objectives)) {
        throw new Error('Respuesta de GPT no tiene el formato esperado: falta array "objectives"');
      }

      // Validar que la respuesta incluya TODOS los objetivos enviados
      if (evaluationResponse.objectives.length !== objectives.length) {
        throw new Error(
          `Respuesta de GPT incompleta: se enviaron ${objectives.length} objetivos pero se recibieron ${evaluationResponse.objectives.length}`
        );
      }

      // Validar cada resultado de evaluación
      const validatedResults: ObjectiveEvaluationResult[] = [];
      const objectiveDescriptions = new Set(objectives.map(obj => obj.description));

      for (const result of evaluationResponse.objectives) {
        // Validar que tenga todos los campos requeridos
        if (!result.description || typeof result.description !== 'string') {
          throw new Error('Respuesta de GPT inválida: falta o es inválido el campo "description"');
        }

        if (typeof result.is_fulfilled !== 'boolean') {
          throw new Error(`Respuesta de GPT inválida: "is_fulfilled" debe ser boolean, recibido: ${typeof result.is_fulfilled}`);
        }

        if (typeof result.confidence !== 'number' || result.confidence < 0 || result.confidence > 1) {
          throw new Error(
            `Respuesta de GPT inválida: "confidence" debe ser número entre 0.0 y 1.0, recibido: ${result.confidence}`
          );
        }

        // Validar que la descripción coincida con uno de los objetivos enviados
        if (!objectiveDescriptions.has(result.description)) {
          throw new Error(
            `Respuesta de GPT inválida: descripción "${result.description}" no coincide con ningún objetivo enviado`
          );
        }

        validatedResults.push({
          description: result.description,
          is_fulfilled: result.is_fulfilled,
          confidence: Math.max(0, Math.min(1, result.confidence)) // Asegurar que esté en rango [0, 1]
        });
      }

      // Validar que no haya duplicados
      const uniqueDescriptions = new Set(validatedResults.map(r => r.description));
      if (uniqueDescriptions.size !== validatedResults.length) {
        throw new Error('Respuesta de GPT inválida: hay objetivos duplicados en la respuesta');
      }

      // Validar que todos los objetivos enviados estén presentes en la respuesta
      for (const objective of objectives) {
        if (!validatedResults.some(r => r.description === objective.description)) {
          throw new Error(
            `Respuesta de GPT incompleta: falta el objetivo "${objective.description}" en la respuesta`
          );
        }
      }

      return validatedResults;

    } catch (error) {
      // console.error('[ObjectiveEvaluation] Error al evaluar objetivos con GPT:', error);
      // Retornar array vacío en caso de error (el código llamador puede usar fallback)
      return [];
    }
  }
}

export const objectiveEvaluationService = new ObjectiveEvaluationService();
export default objectiveEvaluationService;

